/**
 * FormatPicker - Unified Video Format Selection Component
 * 
 * Features:
 * - Step 1: Select aspect ratio (9:16 Vertical, 1:1 Square, Original)
 * - Step 2: Select style within that ratio (Stacked/Blur, Zoom, etc.)
 * - Step 3: Select output resolution (360p, 480p, 720p, 1080p, 1440p, 4K)
 * - CSS-rendered previews using a single reference image
 * 
 * Used in:
 * - ClipEditModal (Change Video Style)
 * - Tool C (Clipper / Assemble Clips)
 * - Pipeline Setup Modal
 */

import React, { useState } from 'react';
import { Monitor, Smartphone, Square, Maximize, Film, ScanFace, CheckCircle2 } from 'lucide-react';

const FACE_TRACK_FORMATS = [
  'face-track-zoom', 'face-track-zoom-3-4', 'face-track-zoom-square',
  'face-track-zoom-landscape', 'split-face-track'
];

// Resolution options with dimensions for different aspect ratios
const RESOLUTION_OPTIONS = [
  { id: '360p', label: '360p', desc: 'Low quality, small file', dimensions: { '9:16': { w: 360, h: 640 }, '3:4': { w: 360, h: 480 }, '1:1': { w: 360, h: 360 }, '16:9': { w: 640, h: 360 } } },
  { id: '480p', label: '480p', desc: 'SD quality', dimensions: { '9:16': { w: 480, h: 854 }, '3:4': { w: 480, h: 640 }, '1:1': { w: 480, h: 480 }, '16:9': { w: 854, h: 480 } } },
  { id: '720p', label: '720p', desc: 'HD quality (recommended)', dimensions: { '9:16': { w: 720, h: 1280 }, '3:4': { w: 720, h: 960 }, '1:1': { w: 720, h: 720 }, '16:9': { w: 1280, h: 720 } }, recommended: true },
  { id: '1080p', label: '1080p', desc: 'Full HD quality', dimensions: { '9:16': { w: 1080, h: 1920 }, '3:4': { w: 1080, h: 1440 }, '1:1': { w: 1080, h: 1080 }, '16:9': { w: 1920, h: 1080 } } },
  { id: '1440p', label: '1440p', desc: '2K quality', dimensions: { '9:16': { w: 1440, h: 2560 }, '3:4': { w: 1440, h: 1920 }, '1:1': { w: 1440, h: 1440 }, '16:9': { w: 2560, h: 1440 } } },
  { id: '4k', label: '4K', desc: 'Ultra HD quality', dimensions: { '9:16': { w: 2160, h: 3840 }, '3:4': { w: 2160, h: 2880 }, '1:1': { w: 2160, h: 2160 }, '16:9': { w: 3840, h: 2160 } } }
];

// Get dimensions for a given resolution and aspect ratio
const getResolutionDimensions = (resolutionId, aspectRatio) => {
  const res = RESOLUTION_OPTIONS.find(r => r.id === resolutionId);
  if (!res) return { w: 720, h: 1280 }; // default 720p 9:16
  const ratio = aspectRatio === 'original' ? '16:9' : aspectRatio;
  return res.dimensions[ratio] || res.dimensions['9:16'];
};

// Format definitions organized by aspect ratio
const FORMAT_CONFIG = {
  'original': {
    label: 'Original',
    description: 'Keep original aspect ratio',
    icon: Monitor,
    styles: [
      { id: 'raw-cuts', label: 'Original', desc: 'No changes, fastest export' },
      { id: 'face-track-zoom-landscape', label: 'Zoom (Face)', desc: 'Center-crop on speaker', premium: true }
    ]
  },
  '9:16': {
    label: '9:16 Vertical',
    description: 'TikTok, Reels, Shorts',
    icon: Smartphone,
    styles: [
      { id: 'stacked-blur', label: 'Blur (Fast)', desc: 'Static blur background, faster' },
      { id: 'stacked-blur-motion', label: 'Blur (Motion)', desc: 'Dynamic blur follows video', premium: true },
      { id: 'center-crop', label: 'Zoom Crop', desc: 'Center crop, full fill' },
      { id: 'portrait-square', label: 'Square (Fast)', desc: '1:1 video on static blur' },
      { id: 'portrait-square-motion', label: 'Square (Motion)', desc: '1:1 video on dynamic blur', premium: true },
      { id: 'split-speaker', label: 'Split (L→R)', desc: 'Left speaker top, Right bottom' },
      { id: 'split-speaker-inv', label: 'Split (R→L)', desc: 'Right speaker top, Left bottom' },
      { id: 'split-face-track', label: 'Split (Face)', desc: 'Auto-center on faces', premium: true },
      { id: 'face-track-zoom', label: 'Zoom (Face)', desc: 'Center-crop on speaker', premium: true }
    ]
  },
  '3:4': {
    label: '3:4 Portrait',
    description: 'Instagram Posts',
    icon: Maximize,
    styles: [
      { id: 'ig-post-blur', label: 'Blur (Fast)', desc: 'Static blur background' },
      { id: 'ig-post-blur-motion', label: 'Blur (Motion)', desc: 'Dynamic blur follows video', premium: true },
      { id: 'ig-post-crop', label: 'Zoom Crop', desc: 'Center crop, full fill' },
      { id: 'face-track-zoom-3-4', label: 'Zoom (Face)', desc: 'Center-crop on speaker', premium: true }
    ]
  },
  '1:1': {
    label: '1:1 Square',
    description: 'Instagram Feed',
    icon: Square,
    styles: [
      { id: 'square-blur', label: 'Blur (Fast)', desc: 'Static blur background' },
      { id: 'square-blur-motion', label: 'Blur (Motion)', desc: 'Dynamic blur follows video', premium: true },
      { id: 'square-zoom', label: 'Zoom Crop', desc: 'Center crop, full fill' },
      { id: 'face-track-zoom-square', label: 'Zoom (Face)', desc: 'Center-crop on speaker', premium: true }
    ]
  }
};

// Get aspect ratio from format ID
const getAspectRatioFromFormat = (formatId) => {
  if (formatId === 'raw-cuts') return 'original';
  if (['stacked-blur', 'stacked-blur-motion', 'center-crop', 'portrait-square', 'portrait-square-motion', 'split-speaker', 'split-speaker-inv', 'split-face-track', 'face-track-zoom'].includes(formatId)) return '9:16';
  if (['ig-post-blur', 'ig-post-blur-motion', 'ig-post-crop', 'portrait-3-4', 'portrait-3-4-motion', 'face-track-zoom-3-4'].includes(formatId)) return '3:4';
  if (['square-blur', 'square-blur-motion', 'square-zoom', 'face-track-zoom-square'].includes(formatId)) return '1:1';
  return '9:16'; // default
};

// Compact icon-based preview renderer with clear aspect ratio visualization
const FormatPreview = ({ formatId, aspectRatio, isSelected, compact = false }) => {
  // Icon-based previews that clearly show the format
  const renderPreviewIcon = () => {
    const iconSize = compact ? 'w-6 h-6' : 'w-8 h-8';
    const containerClass = compact ? 'w-full h-16' : 'w-full h-20';
    
    switch (formatId) {
      case 'raw-cuts':
        // Original 16:9
        return (
          <div className={`${containerClass} bg-dark-300 rounded-lg flex items-center justify-center`}>
            <div className="w-12 h-7 bg-dark-100 rounded border-2 border-cream-400 flex items-center justify-center">
              <Monitor className={`${iconSize} text-cream-400`} />
            </div>
          </div>
        );

      case 'stacked-blur':
        // 9:16 with blur background
        return (
          <div className={`${containerClass} bg-gradient-to-b from-dark-200/50 to-dark-300/50 rounded-lg flex items-center justify-center p-2`}>
            <div className="relative w-8 h-14 bg-dark-400 rounded border border-dark-100 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-blue-500/20 to-purple-500/20" />
              <div className="absolute inset-x-1 top-1/2 -translate-y-1/2 h-4 bg-dark-100 rounded-sm" />
            </div>
          </div>
        );

      case 'center-crop':
        // 9:16 zoom/crop
        return (
          <div className={`${containerClass} bg-gradient-to-br from-gold-600/30 to-amber-500/30 rounded-lg flex items-center justify-center p-2`}>
            <div className="relative w-8 h-14 bg-dark-400 rounded border border-gold-500/50 overflow-hidden">
              <Maximize className="absolute inset-0 m-auto w-5 h-5 text-gold-500/70" />
            </div>
          </div>
        );

      case 'portrait-square':
        // 9:16 with square video
        return (
          <div className={`${containerClass} bg-gradient-to-b from-indigo-600/30 to-purple-600/30 rounded-lg flex items-center justify-center p-2`}>
            <div className="relative w-8 h-14 bg-dark-400 rounded border border-indigo-500/50 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-indigo-500/10 to-purple-500/10" />
              <div className="absolute inset-x-1 top-1/2 -translate-y-1/2 w-6 h-6 bg-dark-100 rounded-sm mx-auto" />
            </div>
          </div>
        );

      case 'split-speaker':
        // Split L→R
        return (
          <div className={`${containerClass} bg-gradient-to-b from-emerald-600/30 to-teal-600/30 rounded-lg flex items-center justify-center p-2`}>
            <div className="relative w-8 h-14 bg-dark-400 rounded border border-emerald-500/50 overflow-hidden flex flex-col">
              <div className="flex-1 bg-emerald-500/20 border-b border-emerald-500/50 flex items-center justify-center">
                <span className="text-[11px] font-bold text-emerald-400">L</span>
              </div>
              <div className="flex-1 bg-teal-500/20 flex items-center justify-center">
                <span className="text-[11px] font-bold text-teal-400">R</span>
              </div>
            </div>
          </div>
        );

      case 'split-speaker-inv':
        // Split R→L
        return (
          <div className={`${containerClass} bg-gradient-to-b from-pink-600/30 to-rose-600/30 rounded-lg flex items-center justify-center p-2`}>
            <div className="relative w-8 h-14 bg-dark-400 rounded border border-pink-500/50 overflow-hidden flex flex-col">
              <div className="flex-1 bg-pink-500/20 border-b border-pink-500/50 flex items-center justify-center">
                <span className="text-[11px] font-bold text-pink-400">R</span>
              </div>
              <div className="flex-1 bg-rose-500/20 flex items-center justify-center">
                <span className="text-[11px] font-bold text-rose-400">L</span>
              </div>
            </div>
          </div>
        );

      case 'stacked-blur-motion':
        // 9:16 with motion blur background
        return (
          <div className={`${containerClass} bg-gradient-to-b from-cyan-600/50 to-blue-600/50 rounded-lg flex items-center justify-center p-2`}>
            <div className="relative w-8 h-14 bg-dark-400 rounded border border-cyan-500/50 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/30 to-blue-500/30 animate-pulse" />
              <div className="absolute inset-x-1 top-1/2 -translate-y-1/2 h-4 bg-dark-100 rounded-sm" />
              <span className="absolute bottom-0.5 right-0.5 text-[6px] font-bold text-cyan-400">M</span>
            </div>
          </div>
        );

      case 'portrait-square-motion':
        // 9:16 with square video + motion blur
        return (
          <div className={`${containerClass} bg-gradient-to-b from-violet-600/50 to-purple-600/50 rounded-lg flex items-center justify-center p-2`}>
            <div className="relative w-8 h-14 bg-dark-400 rounded border border-violet-500/50 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-violet-500/20 to-purple-500/20 animate-pulse" />
              <div className="absolute inset-x-1 top-1/2 -translate-y-1/2 w-6 h-6 bg-dark-100 rounded-sm mx-auto" />
              <span className="absolute bottom-0.5 right-0.5 text-[6px] font-bold text-violet-400">M</span>
            </div>
          </div>
        );

      case 'square-blur':
        // 1:1 with blur
        return (
          <div className={`${containerClass} bg-gradient-to-r from-blue-600/30 to-purple-600/30 rounded-lg flex items-center justify-center p-2`}>
            <div className="relative w-12 h-12 bg-dark-400 rounded border border-blue-500/50 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10" />
              <div className="absolute inset-2 bg-dark-100 rounded-sm" />
            </div>
          </div>
        );

      case 'square-blur-motion':
        // 1:1 with motion blur
        return (
          <div className={`${containerClass} bg-gradient-to-r from-cyan-600/50 to-teal-600/50 rounded-lg flex items-center justify-center p-2`}>
            <div className="relative w-12 h-12 bg-dark-400 rounded border border-cyan-500/50 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-teal-500/20 animate-pulse" />
              <div className="absolute inset-2 bg-dark-100 rounded-sm" />
              <span className="absolute bottom-0.5 right-0.5 text-[6px] font-bold text-cyan-400">M</span>
            </div>
          </div>
        );

      case 'square-zoom':
        // 1:1 zoom
        return (
          <div className={`${containerClass} bg-gradient-to-br from-pink-500/30 to-rose-500/30 rounded-lg flex items-center justify-center p-2`}>
            <div className="relative w-12 h-12 bg-dark-400 rounded border border-pink-500/50 overflow-hidden">
              <Maximize className="absolute inset-0 m-auto w-5 h-5 text-pink-500/70" />
            </div>
          </div>
        );

      case 'ig-post-blur':
      case 'portrait-3-4':
        // 3:4 Instagram post with blur
        return (
          <div className={`${containerClass} bg-gradient-to-b from-fuchsia-600/30 to-pink-600/30 rounded-lg flex items-center justify-center p-2`}>
            <div className="relative w-9 h-12 bg-dark-400 rounded border border-fuchsia-500/50 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-fuchsia-500/10 to-pink-500/10" />
              <div className="absolute inset-1.5 bg-dark-100 rounded-sm" />
            </div>
          </div>
        );

      case 'ig-post-blur-motion':
      case 'portrait-3-4-motion':
        // 3:4 Instagram post with motion blur
        return (
          <div className={`${containerClass} bg-gradient-to-b from-fuchsia-600/50 to-pink-600/50 rounded-lg flex items-center justify-center p-2`}>
            <div className="relative w-9 h-12 bg-dark-400 rounded border border-fuchsia-500/50 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-fuchsia-500/20 to-pink-500/20 animate-pulse" />
              <div className="absolute inset-1.5 bg-dark-100 rounded-sm" />
              <span className="absolute bottom-0.5 right-0.5 text-[6px] font-bold text-fuchsia-400">M</span>
            </div>
          </div>
        );

      case 'ig-post-crop':
      case 'portrait-3-4-crop':
        // 3:4 Instagram post zoom
        return (
          <div className={`${containerClass} bg-gradient-to-br from-orange-500/30 to-amber-500/30 rounded-lg flex items-center justify-center p-2`}>
            <div className="relative w-9 h-12 bg-dark-400 rounded border border-orange-500/50 overflow-hidden">
              <Maximize className="absolute inset-0 m-auto w-4 h-4 text-orange-500/70" />
            </div>
          </div>
        );

      case 'split-face-track':
        // Split with face tracking
        return (
          <div className={`${containerClass} bg-gradient-to-b from-cyan-600/30 to-emerald-600/30 rounded-lg flex items-center justify-center p-2`}>
            <div className="relative w-8 h-14 bg-dark-400 rounded border border-cyan-500/50 overflow-hidden flex flex-col">
              <div className="flex-1 bg-cyan-500/20 border-b border-cyan-500/50 flex items-center justify-center">
                <ScanFace className="w-3 h-3 text-cyan-400" />
              </div>
              <div className="flex-1 bg-emerald-500/20 flex items-center justify-center">
                <ScanFace className="w-3 h-3 text-emerald-400" />
              </div>
            </div>
          </div>
        );

      case 'face-track-zoom':
      case 'face-track-zoom-3-4':
      case 'face-track-zoom-square':
      case 'face-track-zoom-landscape':
        // Face tracking zoom (all aspect ratios)
        return (
          <div className={`${containerClass} bg-gradient-to-br from-cyan-500/30 to-blue-500/30 rounded-lg flex items-center justify-center p-2`}>
            <div className={`relative ${formatId === 'face-track-zoom-square' ? 'w-12 h-12' : formatId === 'face-track-zoom-landscape' ? 'w-14 h-8' : formatId === 'face-track-zoom-3-4' ? 'w-9 h-12' : 'w-8 h-14'} bg-dark-400 rounded border border-cyan-500/50 overflow-hidden`}>
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20" />
              <ScanFace className="absolute inset-0 m-auto w-5 h-5 text-cyan-400" />
            </div>
          </div>
        );

      default:
        return (
          <div className={`${containerClass} bg-dark-300 rounded-lg flex items-center justify-center`}>
            <Monitor className={`${iconSize} text-cream-500`} />
          </div>
        );
    }
  };

  return (
    <div className={`w-full rounded-lg overflow-hidden relative ${isSelected ? 'ring-2 ring-gold-500 ring-offset-2 ring-offset-cream-50 dark:ring-offset-dark-400' : ''}`}>
      {renderPreviewIcon()}
    </div>
  );
};

/**
 * FormatPicker Component - Compact & Visual
 * 
 * @param {string} selectedFormat - Currently selected format ID
 * @param {function} onFormatChange - Callback when format changes
 * @param {string} selectedResolution - Currently selected resolution (default: '720p')
 * @param {function} onResolutionChange - Callback when resolution changes
 * @param {boolean} compact - Use compact layout (for modals)
 * @param {boolean} showResolution - Whether to show resolution picker (default: true)
 */
const FormatPicker = ({
  selectedFormat = 'stacked-blur',
  onFormatChange,
  selectedResolution = '720p',
  onResolutionChange,
  compact = false,
  showResolution = true,
  multiSelect = false,
  selectedFormats = [],
  onMultiFormatToggle,
  // Face detection props
  faceDetectionEnabled = false,
  faceDetectionMode = 'sekali',
  trackingStyle = 'smooth',
  onFaceDetectionChange,
}) => {
  // Determine initial aspect ratio from selected format
  const [selectedAspectRatio, setSelectedAspectRatio] = useState(() =>
    getAspectRatioFromFormat(selectedFormat)
  );

  const handleAspectRatioChange = (ratio) => {
    setSelectedAspectRatio(ratio);
    // Auto-select first style in the new ratio (single-select mode only)
    if (!multiSelect) {
      const firstStyle = FORMAT_CONFIG[ratio].styles[0];
      if (firstStyle && onFormatChange) {
        onFormatChange(firstStyle.id);
      }
    }
  };

  const handleStyleChange = (styleId) => {
    if (multiSelect && onMultiFormatToggle) {
      onMultiFormatToggle(styleId);
    } else if (onFormatChange) {
      onFormatChange(styleId);
    }
  };

  const handleResolutionChange = (resId) => {
    if (onResolutionChange) {
      onResolutionChange(resId);
    }
  };

  const currentConfig = FORMAT_CONFIG[selectedAspectRatio];
  const aspectRatios = Object.keys(FORMAT_CONFIG);

  return (
    <div className="space-y-3">
      {/* Step 1: Aspect Ratio Selection - Inline pills */}
      <div className="space-y-1.5">
        <label className="text-[11px] font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">
          1. Aspect Ratio
        </label>
        <div className="flex gap-2">
          {aspectRatios.map((ratio) => {
            const config = FORMAT_CONFIG[ratio];
            const Icon = config.icon;
            const isSelected = selectedAspectRatio === ratio;

            return (
              <button
                key={ratio}
                onClick={() => handleAspectRatioChange(ratio)}
                className={`flex-1 px-3 py-2 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                  isSelected
                    ? 'border-gold-500 bg-gold-500/10'
                    : 'border-cream-300 dark:border-dark-200 hover:border-gold-500/50'
                }`}
              >
                <Icon size={14} className={isSelected ? 'text-gold-500' : 'text-cream-500 dark:text-cream-400'} />
                <span className={`text-xs font-bold ${isSelected ? 'text-gold-500' : 'text-dark-200 dark:text-cream-300'}`}>
                  {config.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Step 2: Style Selection - Compact grid */}
      <div className="space-y-1.5">
        <label className="text-[11px] font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">
          2. Style
        </label>
        <div className={`grid ${currentConfig.styles.length <= 2 ? 'grid-cols-2' : currentConfig.styles.length <= 3 ? 'grid-cols-3' : compact ? 'grid-cols-3' : 'grid-cols-5'} gap-2`}>
          {currentConfig.styles.map((style) => {
            const isSelected = multiSelect
              ? selectedFormats.includes(style.id)
              : selectedFormat === style.id;

            return (
              <button
                key={style.id}
                onClick={() => handleStyleChange(style.id)}
                className={`rounded-lg border-2 transition-all overflow-hidden relative ${
                  isSelected
                    ? 'border-gold-500 bg-gold-500/5'
                    : 'border-cream-300 dark:border-dark-200 hover:border-gold-500/50'
                }`}
              >
                {/* Multi-select checkmark overlay */}
                {multiSelect && isSelected && (
                  <div className="absolute top-1 right-1 z-10 w-5 h-5 rounded-full bg-gold-500 flex items-center justify-center shadow-lg">
                    <CheckCircle2 size={14} className="text-white" />
                  </div>
                )}
                {/* Icon Preview */}
                <FormatPreview
                  formatId={style.id}
                  aspectRatio={selectedAspectRatio}
                  isSelected={isSelected}
                  compact={compact}
                />
                {/* Label */}
                <div className="px-1.5 py-1.5 text-center bg-cream-100 dark:bg-dark-400/50">
                  <p className={`text-[11px] font-bold leading-tight ${isSelected ? 'text-gold-500' : 'text-dark-200 dark:text-cream-300'}`}>
                    {style.label}
                  </p>
                  <p className="text-[11px] text-cream-500 dark:text-cream-400 leading-tight">{style.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Step 3: Resolution Selection - Compact pills */}
      {showResolution && (
        <div className="space-y-1.5">
          <label className="text-[11px] font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400 flex items-center gap-1">
            <Film size={10} /> 3. Resolution
          </label>
          <div className="flex flex-wrap gap-1.5">
            {RESOLUTION_OPTIONS.map((res) => {
              const isSelected = selectedResolution === res.id;
              const dims = getResolutionDimensions(res.id, selectedAspectRatio);

              return (
                <button
                  key={res.id}
                  onClick={() => handleResolutionChange(res.id)}
                  className={`px-2.5 py-1.5 rounded-lg border transition-all text-center relative ${
                    isSelected
                      ? 'border-gold-500 bg-gold-500/10'
                      : 'border-cream-300 dark:border-dark-200 hover:border-gold-500/50'
                  }`}
                >
                  {res.recommended && (
                    <span className="absolute -top-1 -right-1 px-1 py-0.5 bg-gold-500 text-white text-[6px] font-bold rounded">
                      ★
                    </span>
                  )}
                  <p className={`text-[11px] font-bold ${isSelected ? 'text-gold-500' : 'text-dark-200 dark:text-cream-300'}`}>
                    {res.label}
                  </p>
                  <p className="text-[7px] text-cream-500 dark:text-cream-400">{dims.w}×{dims.h}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Face Detection Controls — shown only for face-track styles */}
      {FACE_TRACK_FORMATS.includes(multiSelect ? null : selectedFormat) && onFaceDetectionChange && (
        <div className="space-y-2 pt-2 border-t border-cream-300 dark:border-dark-200/50">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400 flex items-center gap-1">
              <ScanFace size={10} /> Live Face Detection
            </label>
            <button
              onClick={() => onFaceDetectionChange({ enabled: !faceDetectionEnabled })}
              className={`w-10 h-5 rounded-full transition-all relative ${faceDetectionEnabled ? 'bg-cyan-500' : 'bg-dark-200'}`}
            >
              <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-all ${faceDetectionEnabled ? 'left-5' : 'left-0.5'}`} />
            </button>
          </div>

          {faceDetectionEnabled && (
            <>
              <div className="flex gap-1 bg-cream-200 dark:bg-dark-300/50 p-1 rounded-lg">
                {[
                  { id: 'sekali', label: 'Track Sekali' },
                  { id: 'sepanjang', label: 'Track Sepanjang' },
                ].map(m => (
                  <button
                    key={m.id}
                    onClick={() => onFaceDetectionChange({ mode: m.id })}
                    className={`flex-1 px-2 py-1.5 rounded text-[11px] font-bold transition-all ${
                      faceDetectionMode === m.id ? 'bg-cyan-500/20 text-cyan-400' : 'text-cream-500 hover:text-cream-300'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              {faceDetectionMode === 'sepanjang' && (
                <div className="flex gap-1 bg-cream-200 dark:bg-dark-300/50 p-1 rounded-lg">
                  {[
                    { id: 'smooth', label: 'Smooth' },
                    { id: 'snap', label: 'Snap' },
                  ].map(s => (
                    <button
                      key={s.id}
                      onClick={() => onFaceDetectionChange({ trackingStyle: s.id })}
                      className={`flex-1 px-2 py-1.5 rounded text-[11px] font-bold transition-all ${
                        trackingStyle === s.id ? 'bg-cyan-500/20 text-cyan-400' : 'text-cream-500 hover:text-cream-300'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FormatPicker;
export { FORMAT_CONFIG, getAspectRatioFromFormat, FormatPreview, RESOLUTION_OPTIONS, getResolutionDimensions };
